import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { COMMON } from "@store/theme/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
/* Valtech Custom Code */

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
}
const cookieHeight = 230; // Valtech Custom Code
const cookieHeightTablet = 180; // Valtech Custom Code

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 100,
        cssOverrides: {
            scrim: css`
                box-shadow: 0 0 12px rgba(0, 0, 0, 0.25);
                border: 1px solid ${getColor("common.accent")};
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            {
                                values: [`${cookieHeight}px`, null, `${cookieHeightTablet}px`, "100px"],
                                properties: ["height"],
                            },
                        ],
                        mediaOptions.min,
                    )}
                @media print {
                    display: none;
                }
            `,
            drawerBody: css`
                border-top: 1px solid ${getColor("common.border")};
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            {
                                values: [`${cookieHeight}px`, null, `${cookieHeightTablet}px`, "100px"],
                                properties: ["height"],
                            },
                        ],
                        mediaOptions.min,
                    )}
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 15px;
                margin: 0 auto;
                width: 100%;
                display: flex;
                align-items: center;
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            {
                                values: [`${cookieHeight}px`, null, `${cookieHeightTablet}px`, "100px"],
                                properties: ["height"],
                            },
                        ],
                        mediaOptions.min,
                    )}
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: ${COMMON.width.content}px;
                            `,
                            null,
                            null,
                        ],
                        "min",
                    )}
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            align-items: center;
        `,
    },
    messageText: {
        css: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [`1.25`, `1.5`], properties: ["line-height"] }],
                    mediaOptions.min,
                )}
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin: auto;
                            text-align: center;
                            margin-bottom: 1em;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    acceptGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            align-items: center;
            justify-content: flex-end;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            justify-content: center;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    acceptButton: {
        size: 40,
        css: css`
            line-height: 20px;
        `,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 365 });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };
    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <GridContainer {...styles.container}>
                <GridItem {...styles.messageGridItem}>
                    <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                        {siteMessage("Core_CookiePrivacyPolicy")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.acceptGridItem}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                </GridItem>
                {/* Valtech Custom Code - Removed closeGridItem */}
            </GridContainer>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
